import React, { useContext } from 'react';
import { Route } from 'react-router-dom';

import { GQLAddress, GQLParty, GQLSecurityContext } from '@jewelutions/core-crm-server-api-client-domain';
import PiecesList from '../pieces/PiecesList';
import PieceDetails from '../pieces/PieceDetails';
import AccessoriesList from '../accessories/AccessoriesList';
import AccessoryDetails from '../accessories/AccessoryDetails';
import FontList from '../fonts/FontList';
import FontDetails from '../fonts/FontDetails';
import SymbolCollectionList from '../symbols/SymbolCollectionList';
import SymbolCollectionDetails from '../symbols/SymbolCollectionDetails';
import {
  PlatformMerchantContractsList,
  PlatformMerchantContractsDetails
} from '../platformMerchantContracts';
import { ProducerContractsList } from '../platformProducerContracts';
import { DollarSign, Settings, Triangle } from 'react-feather';

import { PlatformSaleCatalogsList, PlatformSaleCatalogDetails } from '../platformSaleCatalogs';

import { PlatformNavigation } from './PlatformNavigation';

import { RouteType } from '../../../types/routes';
import PieceModelDetails from '../pieces/PieceModelDetails';
import { AccessoryModelDetails } from '../accessories';
import PartyList from '../parties/PartyList';
import PartyDetails from '../parties/PartyDetails';
import JobList from '../jobs/JobList';
import JobDetails from '../jobs/JobDetails/JobDetails';
import Support from '../support/Support';
import PlatformProducerWithPlatformMerchantOrdersList from '../platformProducerWithPlatformMerchantOrders/PlatformProducerWithPlatformMerchantOrdersList';
import PlatformProducerWithPlatformMerchantOrderDetails from '../platformProducerWithPlatformMerchantOrders/PlatformProducerWithPlatformMerchantOrderDetails';
import { AuthContext } from '../../../core/AuthContext';
import { DefaultLayout } from '../../../types-hack';

interface PlatformRoutesProps {}

const PlatformRoutes: React.FC<PlatformRoutesProps> = ({}: PlatformRoutesProps) => {
  const securityContext = useContext(AuthContext).auth!;

  const party: GQLParty = {
    id: securityContext.partyId,
    name: securityContext.partyName,
    type: securityContext.partyType,
    email: securityContext.userEmail,
    address: (undefined as unknown) as GQLAddress
  };

  const sidebarRoutes: RouteType[] = [
    {
      id: 'Platform',
      // path: '/parties',
      path: '/',
      icon: <Settings />,
      children: [
        {
          path: PlatformNavigation.partyList.pathFromParams({}),
          name: 'Parties'
        },
        {
          path: PlatformNavigation.jobList.pathFromParams({}),
          name: 'Jobs'
        }
      ]
    },
    {
      id: 'Orders & pricing',
      // path: PlatformNavigation.platformProducerContracts.pathFromParams({}),
      path: '/',
      icon: <DollarSign />,
      children: [
        {
          path: PlatformNavigation.platformProducerWithPlatformMerchantOrdersList.pathFromParams({}),
          name: 'Platform orders'
        },
        {
          path: PlatformNavigation.platformProducerContracts.pathFromParams({}),
          name: 'Producer Contracts'
        },
        {
          path: PlatformNavigation.platformMerchantContracts.pathFromParams({}),
          name: 'Merchant Contracts'
        },
        {
          path: PlatformNavigation.platformSaleCatalogs.pathFromParams({}),
          name: 'Platform Sale Catalogs'
        }
      ]
    },
    {
      id: 'Design Assets',
      path: '/',
      icon: <Triangle />,
      children: [
        {
          name: 'Piece CADs',
          path: PlatformNavigation.pieceList.pathFromParams({})
        },
        {
          name: 'Accessory CADs',
          path: PlatformNavigation.accessoryList.pathFromParams({})
        },
        {
          name: 'Fonts',
          path: PlatformNavigation.fontList.pathFromParams({})
        },
        {
          name: 'Symbols',
          path: PlatformNavigation.symbolCollectionList.pathFromParams({})
        }
      ]
    }
  ];

  return (
    <React.Fragment>
      <Route
        path={PlatformNavigation.partyList.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Parties List"
            sidebarRoutes={sidebarRoutes}
            helpRoute={PlatformNavigation.support.pathPattern()}
          >
            {<PartyList party={party} />}
          </DefaultLayout>
        )}
      />
      <Route
        path={PlatformNavigation.partyDetails.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Party Details"
            sidebarRoutes={sidebarRoutes}
            helpRoute={PlatformNavigation.support.pathPattern()}
          >
            {<PartyDetails party={party} />}
          </DefaultLayout>
        )}
      />
      <Route
        path={PlatformNavigation.jobList.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Jobs List"
            sidebarRoutes={sidebarRoutes}
            helpRoute={PlatformNavigation.support.pathPattern()}
          >
            {<JobList party={party} />}
          </DefaultLayout>
        )}
      />
      <Route
        path={PlatformNavigation.jobDetails.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Job Details"
            sidebarRoutes={sidebarRoutes}
            hasBackButton
            helpRoute={PlatformNavigation.support.pathPattern()}
          >
            {<JobDetails />}
          </DefaultLayout>
        )}
      />
      <Route
        path={PlatformNavigation.pieceList.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Pieces"
            sidebarRoutes={sidebarRoutes}
            helpRoute={PlatformNavigation.support.pathPattern()}
          >
            {<PiecesList party={party} />}
          </DefaultLayout>
        )}
      />
      <Route
        path={PlatformNavigation.pieceDetails.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Piece Details"
            hasBackButton
            sidebarRoutes={sidebarRoutes}
            helpRoute={PlatformNavigation.support.pathPattern()}
          >
            {<PieceDetails party={party} />}
          </DefaultLayout>
        )}
      />
      <Route
        path={PlatformNavigation.pieceModelDetails.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Piece Model Details"
            hasBackButton
            sidebarRoutes={sidebarRoutes}
            helpRoute={PlatformNavigation.support.pathPattern()}
          >
            {<PieceModelDetails party={party} />}
          </DefaultLayout>
        )}
      />
      <Route
        path={PlatformNavigation.accessoryList.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Accessories"
            sidebarRoutes={sidebarRoutes}
            helpRoute={PlatformNavigation.support.pathPattern()}
          >
            {<AccessoriesList party={party} />}
          </DefaultLayout>
        )}
      />
      <Route
        path={PlatformNavigation.accessoryDetails.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Accessory Details"
            hasBackButton
            sidebarRoutes={sidebarRoutes}
            helpRoute={PlatformNavigation.support.pathPattern()}
          >
            {<AccessoryDetails party={party} />}
          </DefaultLayout>
        )}
      />
      <Route
        path={PlatformNavigation.accessoryModelDetails.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Accessory Model Details"
            hasBackButton
            sidebarRoutes={sidebarRoutes}
            helpRoute={PlatformNavigation.support.pathPattern()}
          >
            {<AccessoryModelDetails party={party} />}
          </DefaultLayout>
        )}
      />
      <Route
        path={PlatformNavigation.fontList.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Fonts"
            sidebarRoutes={sidebarRoutes}
            helpRoute={PlatformNavigation.support.pathPattern()}
          >
            {<FontList party={party} />}
          </DefaultLayout>
        )}
      />
      <Route
        path={PlatformNavigation.fontDetails.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Font Detail"
            hasBackButton
            sidebarRoutes={sidebarRoutes}
            helpRoute={PlatformNavigation.support.pathPattern()}
          >
            {<FontDetails party={party} />}
          </DefaultLayout>
        )}
      />
      <Route
        path={PlatformNavigation.symbolCollectionList.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Symbols"
            sidebarRoutes={sidebarRoutes}
            helpRoute={PlatformNavigation.support.pathPattern()}
          >
            {<SymbolCollectionList party={party} />}
          </DefaultLayout>
        )}
      />
      <Route
        path={PlatformNavigation.symbolCollectionDetails.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Symbol Collection Details"
            hasBackButton
            sidebarRoutes={sidebarRoutes}
            helpRoute={PlatformNavigation.support.pathPattern()}
          >
            {<SymbolCollectionDetails party={party} />}
          </DefaultLayout>
        )}
      />

      <Route
        path={PlatformNavigation.platformProducerWithPlatformMerchantOrdersList.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Platform Orders List"
            sidebarRoutes={sidebarRoutes}
            helpRoute={PlatformNavigation.support.pathPattern()}
          >
            {<PlatformProducerWithPlatformMerchantOrdersList />}
          </DefaultLayout>
        )}
      />
      <Route
        path={PlatformNavigation.platformProducerWithPlatformMerchantOrderDetails.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Platform Orders Details"
            sidebarRoutes={sidebarRoutes}
            helpRoute={PlatformNavigation.support.pathPattern()}
          >
            {<PlatformProducerWithPlatformMerchantOrderDetails />}
          </DefaultLayout>
        )}
      />

      <Route
        path={PlatformNavigation.platformMerchantContracts.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Merchant Contracts List"
            sidebarRoutes={sidebarRoutes}
            helpRoute={PlatformNavigation.support.pathPattern()}
          >
            {<PlatformMerchantContractsList />}
          </DefaultLayout>
        )}
      />
      <Route
        path={PlatformNavigation.platformMerchantContractDetails.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Merchant Contract Details"
            hasBackButton
            sidebarRoutes={sidebarRoutes}
            helpRoute={PlatformNavigation.support.pathPattern()}
          >
            {<PlatformMerchantContractsDetails />}
          </DefaultLayout>
        )}
      />
      <Route
        path={PlatformNavigation.platformProducerContracts.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Producer Contracts List"
            sidebarRoutes={sidebarRoutes}
            helpRoute={PlatformNavigation.support.pathPattern()}
          >
            {<ProducerContractsList party={party} />}
          </DefaultLayout>
        )}
      />
      <Route
        path={PlatformNavigation.platformSaleCatalogs.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Platform Sale Catalogs List"
            sidebarRoutes={sidebarRoutes}
            helpRoute={PlatformNavigation.support.pathPattern()}
          >
            {<PlatformSaleCatalogsList />}
          </DefaultLayout>
        )}
      />
      <Route
        path={PlatformNavigation.platformSaleCatalogDetails.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Platform Sale Catalog Details"
            hasBackButton
            sidebarRoutes={sidebarRoutes}
            helpRoute={PlatformNavigation.support.pathPattern()}
          >
            {<PlatformSaleCatalogDetails />}
          </DefaultLayout>
        )}
      />
      <Route
        path={PlatformNavigation.support.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Support"
            sidebarRoutes={sidebarRoutes}
            helpRoute={PlatformNavigation.support.pathPattern()}
          >
            {<Support party={party} />}
          </DefaultLayout>
        )}
      />
    </React.Fragment>
  );
};

export default PlatformRoutes;
